import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormError } from "@/components/common/ui-bits";
import { AiHint } from "@/components/scan/Scan";
import { api, errMsg } from "@/lib/api";
import { AMOUNT_ERROR, DATE_ERROR, PAYMENT_METHODS, isValidAmount, todayNassau } from "@/lib/format";

const blank = (type = "expense") => ({
  type, date: todayNassau(), amount: "", category_id: "", vendor: "", description: "", payment_method: "cash",
});

const Chip = ({ active, onClick, children, testId, tone = "primary" }) => {
  const on = tone === "income" ? "bg-emerald-600 text-white border-emerald-600"
    : tone === "expense" ? "bg-rose-500 text-white border-rose-500" : "bg-primary text-primary-foreground border-primary";
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
      className={`rounded-full border px-3.5 py-2 text-sm font-medium transition-colors active:scale-[0.97] ${active ? on : "bg-card border-border text-foreground/80 hover:border-foreground/30"}`}>
      {children}
    </button>
  );
};

export function EntryForm({ initial, extraCategory, submitLabel = "Save entry", onSubmit, stickySubmit = false, idPrefix = "entry", ai }) {
  const [form, setForm] = useState(() => (initial ? { ...initial } : blank()));
  const [cats, setCats] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const today = todayNassau();

  useEffect(() => {
    api.get("/categories/active").then((r) => setCats(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  }, []);

  const options = useMemo(() => {
    const list = cats.filter((c) => c.type === form.type);
    if (extraCategory && extraCategory.type === form.type && !list.some((c) => c.id === extraCategory.id)) list.push(extraCategory);
    return list;
  }, [cats, form.type, extraCategory]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setError(""); };
  const setType = (t) => { setForm((f) => ({ ...f, type: t, category_id: "" })); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidAmount(form.amount)) return setError(AMOUNT_ERROR);
    if (!form.date || form.date > today) return setError(DATE_ERROR);
    if (!form.category_id) return setError("Choose a category");
    setSaving(true);
    try {
      await onSubmit({ ...form, amount: String(form.amount).replace(/[,$]/g, "").trim() });
      if (!initial) setForm((f) => ({ ...blank(f.type), date: f.date, payment_method: f.payment_method }));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const isIncome = form.type === "income";
  return (
    <form onSubmit={submit} className="space-y-5" noValidate data-testid={`${idPrefix}-form`}>
      <div className="grid grid-cols-2 gap-1.5 rounded-2xl bg-secondary p-1.5" role="tablist">
        {["expense", "income"].map((t) => (
          <button key={t} type="button" onClick={() => setType(t)} data-testid={`${idPrefix}-type-${t}`} aria-pressed={form.type === t}
            className={`rounded-xl py-2.5 text-sm font-semibold transition-colors ${form.type === t
              ? t === "income" ? "bg-emerald-600 text-white shadow-sm" : "bg-rose-500 text-white shadow-sm"
              : "text-foreground/70 hover:text-foreground"}`}>
            {t === "income" ? "Money in (Income)" : "Money out (Expense)"}
          </button>
        ))}
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-amount`} className="eyebrow">Amount (BSD)</Label>
        <div className={`mt-2 flex items-center rounded-2xl border-2 bg-card px-4 transition-colors focus-within:border-primary ${isIncome ? "border-emerald-200" : "border-rose-200"}`}>
          <span className={`font-num text-2xl font-semibold ${isIncome ? "text-emerald-600" : "text-rose-500"}`}>$</span>
          <input id={`${idPrefix}-amount`} inputMode="decimal" autoComplete="off" placeholder="0.00" value={form.amount}
            onChange={(e) => set("amount", e.target.value)} data-testid={`${idPrefix}-amount-input`}
            className="w-full min-w-0 bg-transparent py-3.5 pl-2 font-num text-3xl font-semibold outline-none placeholder:text-muted-foreground/40" />
        </div>
        <AiHint show={ai?.has("amount")} testId={`${idPrefix}-ai-amount`} />
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-date`} className="eyebrow">Date</Label>
        <Input id={`${idPrefix}-date`} type="date" max={today} value={form.date} onChange={(e) => set("date", e.target.value)}
          className="mt-2 h-12 rounded-xl text-base" data-testid={`${idPrefix}-date-input`} />
        <AiHint show={ai?.has("date")} testId={`${idPrefix}-ai-date`} />
      </div>

      <div>
        <div className="eyebrow mb-2">Category</div>
        <div className="flex flex-wrap gap-2" data-testid={`${idPrefix}-category-picker`}>
          {options.map((c) => (
            <Chip key={c.id} active={form.category_id === c.id} onClick={() => set("category_id", c.id)} tone={form.type}
              testId={`${idPrefix}-category-option-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {c.name}
            </Chip>
          ))}
          {options.length === 0 && <span className="text-sm text-muted-foreground">Loading categories…</span>}
        </div>
        <AiHint show={ai?.has("category_id")} testId={`${idPrefix}-ai-category_id`} />
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-vendor`} className="eyebrow">{isIncome ? "From (optional)" : "Paid to – vendor / payee (optional)"}</Label>
        <Input id={`${idPrefix}-vendor`} list={`${idPrefix}-vendor-list`} value={form.vendor} onChange={(e) => set("vendor", e.target.value)}
          placeholder={isIncome ? "e.g. Walk-in customers" : "e.g. ABC Wholesale"} className="mt-2 h-12 rounded-xl text-base" data-testid={`${idPrefix}-vendor-input`} />
        <datalist id={`${idPrefix}-vendor-list`}>{vendors.map((v) => <option key={v} value={v} />)}</datalist>
        <AiHint show={ai?.has("vendor")} testId={`${idPrefix}-ai-vendor`} />
      </div>

      <div>
        <div className="eyebrow mb-2">Payment method</div>
        <div className="flex flex-wrap gap-2">
          {PAYMENT_METHODS.map((p) => (
            <Chip key={p.value} active={form.payment_method === p.value} onClick={() => set("payment_method", p.value)} testId={`${idPrefix}-payment-${p.value}`}>
              {p.label}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-description`} className="eyebrow">Description (optional)</Label>
        <Textarea id={`${idPrefix}-description`} rows={2} value={form.description} onChange={(e) => set("description", e.target.value)}
          placeholder="What was it for?" className="mt-2 rounded-xl text-base" data-testid={`${idPrefix}-description-input`} />
      </div>

      <div className={stickySubmit ? "sticky bottom-[76px] lg:bottom-4 z-20 space-y-3 bg-background/90 backdrop-blur-md -mx-1 px-1 py-2" : "space-y-3"}>
        <FormError message={error} testId={`${idPrefix}-form-error`} />
        <Button type="submit" disabled={saving} data-testid={`${idPrefix}-submit-button`}
          className={`w-full h-12 rounded-xl text-base font-semibold ${isIncome ? "bg-emerald-600 hover:bg-emerald-700" : "bg-primary hover:bg-primary/90"}`}>
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
